/**
 * One JSON Schema property, drawn as one form field.
 *
 * This is the shape half of `components/studio/actionFields.ts`, split out of it the
 * day a second caller arrived — and a second copy of it would have been the wrong
 * answer, because the two would disagree about `{ amount, currency }` within a
 * session. Two forms in this app are built from a JSON Schema nobody in the SPA
 * wrote:
 *
 *   - the **studio's action form**. `components/studio/actionFields.ts` `fieldsFor`
 *     walks the properties of a published action input — `GET /api/studio/_catalogue`
 *     answers `inputs`, the same document an MCP client reads — and asks this file
 *     for one field per property.
 *   - the **client's widget configuration form**
 *     (`.plans/ai-product-placer/plan/P5-v1-1.md` §Before fan-out 3 and item (d2)). A
 *     widget snapshots its type's `config_schema` when it is created, and an owner or
 *     an admin edits the properties that snapshot marks `x-editor: client`. There is
 *     no action name anywhere in that: the schema is a row in the database.
 *
 * So the split is the one a name can draw, and it is drawn hard. **Nothing here knows
 * that an action exists** — no `HAND_WRITTEN`, no `ACTION_FIELDS`, no `actionCopy.ts`
 * — and the folder is what enforces it: `components/studio` reaches into
 * `components/portal` and this folder never reaches back. What is left over is a
 * function of a field name and a schema node, and that function is `fieldOf`.
 *
 * **It answers `null` for anything it does not recognise**, and that is the whole of
 * its error handling. A nested object is a `money` field or it is `null`; an array of
 * arrays is `null`; a union of shapes is `null`. A caller reads `null` as "somebody's
 * component owns this form" rather than as something to render — `fieldsFor` nulls
 * the whole action on one, and a text box holding `[object Object]` is exactly what
 * that refusal exists to prevent.
 *
 * **Unknown keywords are carried past rather than refused.** `fieldOf` touches
 * `type`, `format`, `enum`, `properties`, `required`, `items`, `anyOf`/`oneOf`,
 * `minLength`, `minItems`, `minimum`/`maximum` and `description`, and nothing else —
 * which is why a config schema's own `title` and `x-editor` annotations reach it and
 * leave it untouched. `fieldsOf`, the walk over a whole schema, reads one of them: a
 * property's `title` is its label where it has one, and its header says why that rule
 * is here and not at each caller. `x-editor` is still nobody's business here — who may
 * edit a property is a question about a **person**, and this file has never heard of
 * one.
 *
 * A `FieldSpec` is a description and not a control: `components/studio/ActionField.tsx`
 * is one control per `FieldWidget`, and `components/studio/actionSchema.ts` turns the
 * same `FieldSpec[]` into the resolver a form is validated by.
 */

import { CURRENCIES, type Currency } from '@fareast/shared'

/* ------------------------------------------------------------- the vocabulary */

/**
 * The widgets a field can be drawn with. `.docs/20` §Before fan-out 2's list, plus
 * the four the real schemas turned out to need.
 */
export type FieldWidget =
  /** A single line. The fallback for a string with nothing else to say about it. */
  | 'text'
  /** A note, a reason, a description — anything with a minimum length over 40. */
  | 'textarea'
  /** A whole number with no unit: seats, attempts, hours back. */
  | 'number'
  /** A quantity, which may be fractional — Odoo management is sold by the hour. */
  | 'qty'
  /** An amount and a currency, rendered as one field and sent as `{ amount, currency }`. */
  | 'money'
  /** `YYYY-MM-DD`. */
  | 'date'
  /** `YYYY-MM`. */
  | 'period'
  /** A reference — `FES-2026-047`, `WO-2026-008`, `TSK-1042`. */
  | 'ref'
  /** A client, chosen from the book rather than typed as a slug. */
  | 'client'
  /** A studio member or an agent, chosen from the studio's own directory. */
  | 'assignee'
  /** A service, chosen from the rate card rather than typed as a code. */
  | 'service'
  /** A day **and a time**, as an instant. `Rfc3339Schema` on the wire. */
  | 'datetime'
  /** One of a closed list. */
  | 'select'
  /** A boolean. */
  | 'checkbox'
  /** Free-text strings, entered one per line. */
  | 'tags'
  /** Several of a closed list. */
  | 'multiselect'
  /** Repeatable rows, each row a small form of the widgets above. */
  | 'rows'
  /** A boolean, a number or a string — how `ACTION_FIELDS` draws `settings_set`'s value. */
  | 'scalar'

export type FieldOption = { readonly value: string; readonly label: string }

export type FieldSpec = {
  /** The input field this writes. Dotted names are not supported and never appear. */
  readonly name: string
  readonly widget: FieldWidget
  readonly label: string
  /** The muted line under the control. The schema's own `description` by default. */
  readonly hint?: string
  /** Present for `select` and `multiselect`. */
  readonly options?: readonly FieldOption[]
  /**
   * True for a `select` over **numbers** — `market_tier` is `{ type: 'number', enum:
   * [1, 2, 3] }`.
   *
   * A `<select>`'s value is a string whatever the schema says, so without this the
   * form would send `"1"` to a field that takes `1` and the API would refuse a tier
   * the reader had chosen from a list of three. `ActionForm` `wireValue` reads it.
   */
  readonly numeric?: boolean
  /** Present for `rows`: the fields of one row. Never itself contains a `rows`. */
  readonly fields?: readonly FieldSpec[]
  /**
   * The repeater button's own words — "Add a milestone". `rows` only.
   *
   * Written rather than derived, because deriving it produced **"Add add ons
   * (optional)"**: the button was `Add ${label.toLowerCase()}` over the label the
   * field is drawn with, which already carries the optional suffix, and `labelOf`
   * had already turned `add_ons` into "Add ons". A button is a verb and a noun and
   * there is no rule that turns a plural field name into one. `actionCopy.ts`
   * `add` is where each is written; `ActionField` falls back to the label when a
   * repeater has no entry yet.
   */
  readonly addLabel?: string
  /** False when the schema says the field is required. */
  readonly optional: boolean
  /**
   * The whole sentence a blank required field is refused with — `actionCopy.ts`.
   *
   * A refusal replaces the hint while it is showing (CONTRACT.md §2.8 has one muted
   * line per field), so it has to carry the guidance rather than restate the field
   * name. `actionSchema.ts` is what puts it on the resolver.
   */
  readonly required?: string
  /** The currency a `money` field starts on. */
  readonly currency?: Currency
  /** HTML `min` / form floor for a free number box (not used when drawn as a select). */
  readonly min?: number
  /** HTML `max` / form ceiling for a free number box. */
  readonly max?: number
  /**
   * True when the published schema takes `null` for this field — zod's
   * `anyOf: [X, { type: 'null' }]`.
   *
   * It is not the same thing as `optional`, and treating the two as one was a defect:
   * `tasks_update` is a partial update, so a field **left out** means *unchanged* and
   * `null` is the only thing that means *take the date off it*. A form that sent
   * nothing for an emptied box answered 200 and left the date where it was.
   * `schemaForm.ts` `clearedOf` is the one reader, and its header is the rule.
   */
  readonly nullable?: boolean
  /**
   * The schema's own `minLength`, where it states one. `text` and `textarea` only.
   *
   * Carried for the same rule: a free-text field with no floor is emptied by sending
   * `''` — `instructions`, `acceptance` — and one with a floor cannot be emptied at
   * all, which is a title's case (`minLength: 3`).
   */
  readonly minLength?: number
  /** The schema's own `minItems`, where it states one. `tags`, `multiselect`, `rows`. */
  readonly minItems?: number
}

/* --------------------------------------------------------------- the rules */

/**
 * Fields whose widget is decided by their **name**, whatever their shape says.
 *
 * Every one of these is a string in the schema — a slug, a `YYYY-MM`, a reference —
 * and a text box for each would be a form that only somebody who already knows the
 * data can fill in. The rule is by exact field name, because these names are the
 * vocabulary `ARCHITECTURE.md` §Naming fixed: an action that means "the client" calls
 * it `client`, and one that does not, does not.
 */
const BY_NAME: Readonly<Record<string, FieldWidget>> = {
  /**
   * **Exactly `client`, not "contains client".** `calls_set_availability_rules` takes
   * `client_earliest_local` and `client_latest_local`, which are wall times in the
   * client's zone rather than slugs — a `includes('client')` rule would draw both as
   * a client picker and send the studio's booking window as an account name.
   */
  client: 'client',
  period: 'period',
  /**
   * A rate-card service, by code. Session 24 (R3-09): the box read "The service this
   * is work on — `"video_editing"`", which is a code quoted at somebody who has the
   * card in front of them on three other screens. Both spellings, because
   * `work_orders_*` calls it `service` and `tasks_*` calls it `service_code`, and a
   * suffix rule would also catch a field that is not one.
   */
  service: 'service',
  service_code: 'service',
}

/**
 * A reference is any field called `ref` or ending in `_ref`.
 *
 * A suffix rather than a list, because the list was already wrong: `plan_ref` and
 * `extra_work_order_ref` exist and were not on it, and both would have been drawn as
 * a bare text box. `ARCHITECTURE.md` §Naming is what makes the suffix safe — "every
 * tool addresses entities by human ref, never UUID" — and `parseRef` knows the kind
 * from the value, so the widget does not need to be told which.
 *
 * **Singular only.** `context_refs` and `output_refs` are arrays and are drawn as a
 * list of lines by the array branch; a suffix rule that caught the plural would put a
 * single-line box over a field that takes several.
 */
export function isRefField(name: string): boolean {
  return name === 'ref' || name.endsWith('_ref')
}

/** A string field whose name ends in one of these is a day. */
const DATE_SUFFIXES = ['_on', '_date'] as const

/**
 * A string field whose name ends in one of these gets a textarea, not a box.
 *
 * `scope` is on the list and is the one that is not a suffix rule at heart: it is a
 * **paragraph the client reads on the quote**, and its two forms disagreed about
 * that. `work_orders_draft.scope` has a `minLength` over the threshold below and got
 * a textarea; `work_orders_update.scope` has none and got a single line, so *New work
 * order* and *Revise* asked for the same prose in two different boxes and the second
 * one hid everything past the width of the field. The widget follows the field, not
 * whichever of its two schemas happened to set a minimum.
 */
const LONG_SUFFIXES = [
  'note',
  'reason',
  'description',
  'instructions',
  'text',
  'body',
  'scope',
] as const

/** Below this, a string with a minimum length is still one line. */
const TEXTAREA_MIN_LENGTH = 40

/* ---------------------------------------------------------- the introspection */

/** A JSON Schema node, as far as anything here reads one. */
export type SchemaNode = Record<string, unknown>

/**
 * One field, from its schema node.
 *
 * `nestable` is false inside a `rows` widget: a row may hold a `tags` list, and it
 * may not hold another table. That is the one-level rule, stated once.
 */
export function fieldOf(
  name: string,
  raw: SchemaNode,
  optional: boolean,
  nestable: boolean,
): FieldSpec | null {
  const node = withoutNull(raw)
  const label = labelOf(name)
  const hint = descriptionOf(raw) ?? descriptionOf(node)
  const base = {
    name,
    label,
    optional,
    ...(hint === undefined ? {} : { hint }),
    // Read off the wrapper, before `withoutNull` has thrown the answer away.
    ...(admitsNull(raw) ? { nullable: true } : {}),
  }

  /*
   * An instant is a day **and a time**, and never a box somebody types into.
   *
   * `format: 'date-time'` is what `Rfc3339Schema` publishes, so this is the schema's
   * own answer rather than a suffix rule. R3-09: `due_at` was free text and
   * `next Friday` came back as the raw Zod "Invalid ISO datetime" — a refusal about a
   * wire format, at somebody who was answering the question the label asked.
   */
  if (node['type'] === 'string' && node['format'] === 'date-time') {
    return { ...base, widget: 'datetime' }
  }

  /*
   * The name decides, but only over a string. `money_ref` is a string and gets the
   * reference box; a field that happened to share a name and be an array or an object
   * falls through to its own shape rather than being drawn as one line of text.
   */
  const type = node['type']
  if (type === 'string') {
    const named = BY_NAME[name] ?? (isRefField(name) ? ('ref' as const) : undefined)
    if (named !== undefined) return { ...base, widget: named }
  }

  if (type === 'boolean') return { ...base, widget: 'checkbox' }

  if (type === 'string') {
    const options = optionsOf(node)
    if (options !== null) return { ...base, widget: 'select', options }
    if (DATE_SUFFIXES.some((suffix) => name.endsWith(suffix))) return { ...base, widget: 'date' }
    const minLength = floorOf(node['minLength'])
    return {
      ...base,
      widget: isLong(name, node) ? 'textarea' : 'text',
      ...(minLength === undefined ? {} : { minLength }),
    }
  }

  if (type === 'integer' || type === 'number') {
    /*
     * A number with an `enum` is a closed list and is drawn as one — `market_tier` is
     * `{ type: 'number', enum: [1, 2, 3] }` and reading only a string's enum drew it
     * as a spinner over three legal values and every illegal one between them.
     *
     * An integer with a **small** min…max range (priority 1–5) is the same kind of
     * closed list: a spinner lets the reader type −6, which the API then refuses.
     */
    const options = numberOptionsOf(node) ?? integerRangeOptions(node)
    if (options !== null) return { ...base, widget: 'select', options, numeric: true }
    return {
      ...base,
      widget: type === 'integer' ? 'number' : 'qty',
      ...(boundsOf(node) ?? {}),
    }
  }

  if (type === 'object') {
    return isMoney(node) ? { ...base, widget: 'money', currency: CURRENCIES[0] } : null
  }

  if (type === 'array') {
    const items = withoutNull(recordOf(node['items']) ?? {})
    const minItems = floorOf(node['minItems'])
    const least = minItems === undefined ? {} : { minItems }
    if (items['type'] === 'string') {
      const options = optionsOf(items)
      return options === null
        ? { ...base, widget: 'tags', ...least }
        : { ...base, widget: 'multiselect', options, ...least }
    }
    if (items['type'] === 'object' && nestable && !isMoney(items)) {
      const fields = rowFieldsOf(items)
      return fields === null ? null : { ...base, widget: 'rows', fields, ...least }
    }
    return null
  }

  // What is left is a union. A union of scalars is a `scalar`; a union of shapes is
  // somebody's component (`HAND_WRITTEN`), and answering null here is what says so.
  return isScalarUnion(node) ? { ...base, widget: 'scalar' } : null
}

export type FieldsOptions = {
  /**
   * Property names to leave out.
   *
   * One caller passes two names, and both are fields a **program** fills in:
   * `actionFields.ts` drops `idempotency_key`, which `defineAction` adds to every
   * mutating action and `useStudioAction` generates, and `if_unchanged_since`, which
   * is the stamp of the copy a change was made from. A form that asked for either
   * would be asking the reader for something only the page can know.
   */
  readonly skip?: readonly string[]
}

/**
 * **Every property of a schema, as fields — the walk both of this file's callers
 * make**, and the reason it is here rather than in either of them.
 *
 * `fieldOf` answers one property; a form is all of them. Reading `properties` and
 * `required` off the root, deciding what `optional` means, and refusing the whole
 * schema when one property cannot be drawn are four decisions, and they were about
 * to be written twice — once in `components/studio/actionFields.ts` `fieldsFor` over
 * a published action input, and once in the client's widget configuration form over
 * a widget's snapshotted `config_schema` (`P5-v1-1.md` item (d2)). Two copies of
 * "what does `required` mean" is how the studio's form and the client's form come to
 * disagree about which box may be left empty.
 *
 * **Null for one undrawable property, not a form with a hole in it.** `fieldOf`'s own
 * `null` means "somebody's component owns this"; a schema containing one is a schema
 * this cannot draw, whole. A caller renders the reason.
 *
 * ## The label is the property's `title` where it has one
 *
 * `fieldOf` derives a label from the property **name** — `labelOf('site_daily')` is
 * "Site daily" — because a published action input has no `title` anywhere in it
 * (`z.toJSONSchema` writes descriptions, not titles) and a machine name spelled in
 * English is the best that can be done with one. A **config** schema is the opposite
 * case: `title` is *mandatory* there (`packages/shared/src/schemas/widgetConfig.ts`
 * refuses a property without one — "it is the label the client reads"), and "Previews
 * per day" is what the client and the studio are both shown. So a `title` wins where
 * there is one, and where there is none nothing changes.
 *
 * It is applied here rather than at each caller for the same reason the walk is: the
 * studio's form over a widget's snapshot and the client's form over the same snapshot
 * must not label one property two ways.
 */
export function fieldsOf(schema: SchemaNode, options: FieldsOptions = {}): FieldSpec[] | null {
  const properties = recordOf(schema['properties'])
  if (properties === null) return null
  const required = new Set(stringsOf(schema['required']))
  const skip = new Set(options.skip ?? [])

  const fields: FieldSpec[] = []
  for (const [name, node] of Object.entries(properties)) {
    if (skip.has(name)) continue
    const property = recordOf(node) ?? {}
    const field = fieldOf(name, property, !required.has(name), true)
    if (field === null) return null
    fields.push(titled(field, property))
  }
  return fields
}

/**
 * The same schema with only the properties a predicate keeps, `required` narrowed
 * with them — or null when it keeps none.
 *
 * A form that draws part of a schema has to be **validated** by part of it too, and
 * the two parts must be the same part. The client's widget configuration form draws
 * the properties a config schema marks `x-editor: client` and resolves with
 * `configSchemaToZod` over what this answers (`P5-v1-1.md` item (d2)); filtering the
 * fields and validating against the whole schema would refuse a save for a `required`
 * property the reader is not allowed to see.
 *
 * Everything else about the root is kept, and `additionalProperties: false` is the
 * one that matters: it is what makes the narrowed schema refuse a key that is not on
 * the form, which is the same answer the server gives for the same reason.
 *
 * **`required` is intersected rather than carried.** A property left out that the
 * root still demands would be a schema nothing can satisfy — a resolver that refuses
 * every submit, with the failure on a field that is not on the screen.
 */
export function narrowedTo(schema: SchemaNode, keep: (name: string) => boolean): SchemaNode | null {
  const properties = recordOf(schema['properties'])
  if (properties === null) return null

  const kept = Object.fromEntries(Object.entries(properties).filter(([name]) => keep(name)))
  const names = Object.keys(kept)
  if (names.length === 0) return null

  return {
    ...schema,
    properties: kept,
    required: stringsOf(schema['required']).filter((name) => keep(name)),
  }
}

/** The label the property asked for, or the one its name produced. */
function titled(field: FieldSpec, node: SchemaNode): FieldSpec {
  const title = node['title']
  return typeof title === 'string' && title.trim() !== '' ? { ...field, label: title } : field
}

/** The fields of one repeatable row, or null when a row holds a table of its own. */
function rowFieldsOf(items: SchemaNode): FieldSpec[] | null {
  const properties = recordOf(items['properties'])
  if (properties === null) return null
  const required = new Set(stringsOf(items['required']))

  const fields: FieldSpec[] = []
  for (const [name, node] of Object.entries(properties)) {
    const field = fieldOf(name, recordOf(node) ?? {}, !required.has(name), false)
    if (field === null) return null
    fields.push(field)
  }
  return fields.length === 0 ? null : fields
}

/* -------------------------------------------------------------- the readers */

/**
 * The one branch of `anyOf: [X, { type: 'null' }]`, or the node itself.
 *
 * zod writes a nullable field that way, and every one of them is a field whose widget
 * is X's — a box is drawn the same whether or not `null` is one of its answers.
 * **Drawn** the same, and not *sent* the same: `admitsNull` below is where the half
 * this throws away is kept, because a form that treated nullable and optional as one
 * thing sent nothing for a date somebody had just taken off a task.
 */
function withoutNull(node: SchemaNode): SchemaNode {
  const branches = branchesOf(node)
  if (branches === null) return node
  const real = branches.filter((branch) => branch['type'] !== 'null')
  return real.length === 1 ? (real[0] ?? node) : node
}

/**
 * True when `null` is one of the answers a node takes.
 *
 * `anyOf` or `oneOf` with a `{ type: 'null' }` branch, which is how `z.toJSONSchema`
 * writes `.nullable()` and is the only way the recorded catalogue says it — no input
 * in it carries a `type` array or a `nullable` keyword. A `type: ['string', 'null']`
 * array is the other spelling JSON Schema allows and is not read here, for the reason
 * it is not read anywhere in this file: `fieldOf` matches `type` against a string, so
 * such a node draws nothing and there is no field to say it about.
 */
function admitsNull(node: SchemaNode): boolean {
  return branchesOf(node)?.some((branch) => branch['type'] === 'null') ?? false
}

/**
 * A `minLength` or a `minItems` that says something: a whole number above zero.
 *
 * Zero is each keyword's own default and is how a schema says "no floor", so it is
 * answered as absent — a `FieldSpec` that carried `minLength: 0` would be stating a
 * rule the schema did not make.
 */
function floorOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined
}

function branchesOf(node: SchemaNode): SchemaNode[] | null {
  for (const key of ['anyOf', 'oneOf'] as const) {
    const value = node[key]
    if (Array.isArray(value)) {
      const records = value
        .map((one) => recordOf(one))
        .filter((one): one is SchemaNode => one !== null)
      if (records.length === value.length) return records
    }
  }
  return null
}

/** True for `boolean | number | string` and its neighbours: no branch is a shape. */
function isScalarUnion(node: SchemaNode): boolean {
  const branches = branchesOf(node)
  if (branches === null) return false
  return branches.every(
    (branch) =>
      branch['type'] === 'boolean' ||
      branch['type'] === 'number' ||
      branch['type'] === 'integer' ||
      branch['type'] === 'string',
  )
}

/** `{ amount, currency }` and nothing else — `MoneySchema`, as JSON Schema. */
function isMoney(node: SchemaNode): boolean {
  const properties = recordOf(node['properties'])
  if (properties === null) return false
  const keys = Object.keys(properties).sort()
  return keys.length === 2 && keys[0] === 'amount' && keys[1] === 'currency'
}

/**
 * A numeric enum's options, as strings — a `<select>`'s value is always a string.
 *
 * The label is the number itself rather than `labelOf`: `Tier 1` is the field's job
 * (its own `label`), and "1" is what the reader is choosing between.
 */
function numberOptionsOf(node: SchemaNode): FieldOption[] | null {
  const values = node['enum']
  if (!Array.isArray(values) || values.length === 0) return null
  const numbers = values.filter((one): one is number => typeof one === 'number')
  if (numbers.length !== values.length) return null
  return numbers.map((value) => ({ value: String(value), label: String(value) }))
}

/**
 * An integer min…max of a handful of values — task priority is 1–5.
 *
 * Larger spans (limit 1–100) stay a number box: a hundred-option select is worse
 * than a spinner with `min`/`max` on the input.
 */
const RANGE_SELECT_MAX_SPAN = 10

function integerRangeOptions(node: SchemaNode): FieldOption[] | null {
  if (node['type'] !== 'integer') return null
  const bounds = boundsOf(node)
  if (bounds === null) return null
  const { min, max } = bounds
  if (max - min > RANGE_SELECT_MAX_SPAN) return null
  const options: FieldOption[] = []
  for (let value = min; value <= max; value += 1) {
    options.push({ value: String(value), label: String(value) })
  }
  return options
}

function boundsOf(node: SchemaNode): { readonly min: number; readonly max: number } | null {
  const min = node['minimum']
  const max = node['maximum']
  if (typeof min !== 'number' || typeof max !== 'number') return null
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  if (!Number.isInteger(min) || !Number.isInteger(max) || max < min) return null
  return { min, max }
}

function optionsOf(node: SchemaNode): FieldOption[] | null {
  const values = node['enum']
  if (!Array.isArray(values) || values.length === 0) return null
  const strings = values.filter((one): one is string => typeof one === 'string')
  if (strings.length !== values.length) return null
  return strings.map((value) => ({ value, label: labelOf(value) }))
}

function isLong(name: string, node: SchemaNode): boolean {
  if (LONG_SUFFIXES.some((suffix) => name === suffix || name.endsWith(`_${suffix}`))) return true
  const min = node['minLength']
  return typeof min === 'number' && min >= TEXTAREA_MIN_LENGTH
}

function descriptionOf(node: SchemaNode): string | undefined {
  const value = node['description']
  return typeof value === 'string' && value !== '' ? value : undefined
}

/**
 * `next_retry_at` → `Next retry at`; `card_declined` → `Card declined`.
 *
 * One rule for a field name and for an enum member, because they are the same kind of
 * token and a second rule would produce "Card_declined" in one place and not the
 * other. `format/useFormat.ts` is not the answer here: this is a machine identifier
 * becoming English, not a value becoming a rendering.
 */
export function labelOf(token: string): string {
  const words = token.split('_').filter((word) => word !== '')
  if (words.length === 0) return token
  const [first = '', ...rest] = words
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ')
}

/**
 * A JSON Schema node, or null for anything that is not one.
 *
 * Exported for the one caller that walks a schema's **root** rather than a property
 * of it: `actionFields.ts` `fieldsFor` reads `properties` and `required` itself
 * before handing each property here, and a second copy of these four lines is how
 * the two would come to disagree about whether an array is an object.
 */
export function recordOf(value: unknown): SchemaNode | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as SchemaNode)
    : null
}

/** The strings in a `required` array, ignoring anything in it that is not one. */
export function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string') : []
}
